import type { Dictionary } from '@/i18n/dictionary'
import type { Locale } from '@/i18n/locales'
import { DEFAULT_LOCALE, LOCALES, LOCALE_META } from '@/i18n/locales'
import { getDictionary } from '@/i18n'
import type { Route, RouteKind } from '@/app/routes'
import { routePath } from '@/app/routes'
import { SERVICE_IDS } from '@/content/services'
import { siteConfig } from '@/lib/site-config'

export interface MetaTag {
  name?: string
  property?: string
  content: string
}

export interface LinkTag {
  rel: string
  href: string
  hrefLang?: string
}

export interface HeadData {
  lang: string
  title: string
  description: string
  keywords: string
  robots: string
  canonical: string
  metas: MetaTag[]
  links: LinkTag[]
  jsonLd: string
}

export const absoluteUrl = (path: string): string =>
  `${siteConfig.url}${path.startsWith('/') ? path : `/${path}`}`

const metaFor = (kind: Route['kind'], dictionary: Dictionary) => {
  if (kind === 'privacy') return dictionary.meta.privacy
  if (kind === 'legal') return dictionary.meta.legal
  if (kind === 'mediusPrivacy') return dictionary.meta.mediusPrivacy
  if (kind === 'mediusDeletion') return dictionary.meta.mediusDeletion
  if (kind === 'mediusTerms') return dictionary.meta.mediusTerms
  if (kind === 'sparsaPrivacy') return dictionary.meta.sparsaPrivacy
  if (kind === 'sparsaDeletion') return dictionary.meta.sparsaDeletion
  if (kind === 'sparsaTerms') return dictionary.meta.sparsaTerms
  if (kind === 'sparsaAcceptableUse') return dictionary.meta.sparsaAcceptableUse
  if (kind === 'notFound') return dictionary.meta.notFound
  return dictionary.meta.home
}

const alternateLinks = (kind: RouteKind): LinkTag[] => [
  ...LOCALES.map((locale) => ({
    rel: 'alternate',
    href: absoluteUrl(routePath(kind, locale)),
    hrefLang: LOCALE_META[locale].hrefLang,
  })),
  {
    rel: 'alternate',
    href: absoluteUrl(routePath(kind, DEFAULT_LOCALE)),
    hrefLang: 'x-default',
  },
]

const organisationSchema = (dictionary: Dictionary, locale: Locale) => ({
  '@type': 'ProfessionalService',
  '@id': `${siteConfig.url}/#organization`,
  name: siteConfig.name,
  legalName: siteConfig.legalName,
  url: siteConfig.url,
  address: {
    '@type': 'PostalAddress',
    streetAddress: siteConfig.address.street,
    postalCode: siteConfig.address.postalCode,
    addressLocality: siteConfig.address.city,
    addressCountry: siteConfig.address.countryCode,
  },
  identifier: {
    '@type': 'PropertyValue',
    propertyID: 'MBO',
    name: dictionary.legal.mboLabel,
    value: siteConfig.register.mbo,
  },
  description: dictionary.meta.home.description,
  email: siteConfig.salesEmail,
  telephone: siteConfig.phone,
  foundingDate: String(siteConfig.foundingYear),
  inLanguage: LOCALE_META[locale].htmlLang,
  image: absoluteUrl(siteConfig.ogImage),
  logo: absoluteUrl('/favicon.svg'),
  areaServed: ['HR', 'SI', 'RS', 'DE', 'IT', 'AT'],
  knowsLanguage: LOCALES.map((item) => LOCALE_META[item].htmlLang),
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: dictionary.services.title,
    itemListElement: SERVICE_IDS.map((id, index) => ({
      '@type': 'Offer',
      position: index + 1,
      itemOffered: {
        '@type': 'Service',
        name: dictionary.services.items[id].title,
        description: dictionary.services.items[id].body,
      },
    })),
  },
})

const websiteSchema = (dictionary: Dictionary, locale: Locale) => ({
  '@type': 'WebSite',
  '@id': `${siteConfig.url}/#website`,
  url: siteConfig.url,
  name: siteConfig.name,
  description: dictionary.meta.home.description,
  inLanguage: LOCALE_META[locale].htmlLang,
  publisher: { '@id': `${siteConfig.url}/#organization` },
})

const webPageSchema = (route: Route, dictionary: Dictionary, canonical: string) => {
  const pageMeta = metaFor(route.kind, dictionary)
  return {
    '@type':
      route.kind === 'privacy' || route.kind === 'mediusPrivacy' || route.kind === 'sparsaPrivacy'
        ? 'PrivacyPolicy'
        : 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: pageMeta.title,
    description: pageMeta.description,
    inLanguage: LOCALE_META[route.locale].htmlLang,
    isPartOf: { '@id': `${siteConfig.url}/#website` },
    about: { '@id': `${siteConfig.url}/#organization` },
  }
}

const breadcrumbSchema = (route: Route, dictionary: Dictionary) => {
  const home = {
    '@type': 'ListItem',
    position: 1,
    name: dictionary.nav.home,
    item: absoluteUrl(routePath('home', route.locale)),
  }
  if (route.kind === 'home' || route.kind === 'notFound') {
    return { '@type': 'BreadcrumbList', itemListElement: [home] }
  }
  const titles: Record<Exclude<RouteKind, 'home'>, string> = {
    privacy: dictionary.privacy.title,
    legal: dictionary.legal.title,
    mediusPrivacy: dictionary.mediusPrivacy.title,
    mediusDeletion: dictionary.mediusDeletion.title,
    mediusTerms: dictionary.mediusTerms.title,
    sparsaPrivacy: dictionary.sparsaPrivacy.title,
    sparsaDeletion: dictionary.sparsaDeletion.title,
    sparsaTerms: dictionary.sparsaTerms.title,
    sparsaAcceptableUse: dictionary.sparsaAcceptableUse.title,
  }
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [
      home,
      {
        '@type': 'ListItem',
        position: 2,
        name: titles[route.kind],
        item: absoluteUrl(routePath(route.kind, route.locale)),
      },
    ],
  }
}

export const buildHead = (route: Route): HeadData => {
  const dictionary = getDictionary(route.locale)
  const pageMeta = metaFor(route.kind, dictionary)
  const kind: RouteKind = route.kind === 'notFound' ? 'home' : route.kind
  const canonical = absoluteUrl(routePath(kind, route.locale))
  const ogImage = absoluteUrl(siteConfig.ogImage)
  const localeMeta = LOCALE_META[route.locale]

  const graph = [
    organisationSchema(dictionary, route.locale),
    websiteSchema(dictionary, route.locale),
    webPageSchema(route, dictionary, canonical),
    breadcrumbSchema(route, dictionary),
  ]

  return {
    lang: localeMeta.htmlLang,
    title: pageMeta.title,
    description: pageMeta.description,
    keywords: dictionary.meta.home.keywords.join(', '),
    robots:
      route.kind === 'notFound'
        ? 'noindex, follow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    canonical,
    metas: [
      { name: 'description', content: pageMeta.description },
      { name: 'keywords', content: dictionary.meta.home.keywords.join(', ') },
      { name: 'author', content: siteConfig.legalName },
      { name: 'theme-color', content: siteConfig.themeColor },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: siteConfig.name },
      { property: 'og:title', content: pageMeta.title },
      { property: 'og:description', content: pageMeta.description },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: ogImage },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: siteConfig.name },
      { property: 'og:locale', content: localeMeta.hrefLang.replace('-', '_') },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageMeta.title },
      { name: 'twitter:description', content: pageMeta.description },
      { name: 'twitter:image', content: ogImage },
    ],
    links: [{ rel: 'canonical', href: canonical }, ...alternateLinks(kind)],
    jsonLd: JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }),
  }
}
